package co.maytes.standupbot.dashboard.features.analytics

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.svg.Svg
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgCircle
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgDefs
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgGroup
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgLine
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgLinearGradient
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgPath
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgPolygon
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgPolyline
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgRect
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgStop
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgText
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideUsers
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.shared.dto.analytics.ParticipationDto
import co.maytes.standupbot.shared.dto.analytics.UserParticipation
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier

private const val WIDTH = 800
private const val HEIGHT = 260
private const val PAD_L = 48
private const val PAD_R = 16
private const val PAD_T = 16
private const val PAD_B = 40

@Composable
fun ParticipationChart(dto: ParticipationDto) {
    val users = dto.users
    val isEmpty = users.isEmpty()

    val legend = listOf(
        LegendItem("Active", ChartPalette.at(0)),
        LegendItem("Opted out", Palette.textTertiary.cssRgb()),
    )

    ChartShell(
        eyebrow = "PARTICIPATION",
        title = "Standup participation rate",
        subtitle = "Bars grouped by month — opted-out members show in grey.",
        isEmpty = isEmpty,
        legend = legend,
        emptyTitle = "No participation data yet",
        emptyMessage = "Once members start contributing to standups, their monthly participation rate appears here.",
        emptyIcon = { LucideUsers(size = 26) },
    ) {
        var tooltip by remember { mutableStateOf<BarHover?>(null) }
        val drawn = rememberChartIntroPlayed()

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier { property("position", "relative") },
        ) {
            Svg(attrs = {
                attr("viewBox", "0 0 $WIDTH $HEIGHT")
                attr("width", "100%")
                attr("height", "${HEIGHT}px")
                attr("role", "img")
                attr("aria-label", "Team participation percentages")
            }) {
                renderParticipationGrid()

                val months = users.map { it.month }.distinct().sorted()
                val byMonth = users.groupBy { it.month }
                val totalBars = users.size
                val groupGap = 16
                val availableWidth = WIDTH - PAD_L - PAD_R - (groupGap * (months.size - 1).coerceAtLeast(0))
                val groupWidth = if (months.isEmpty()) 0 else availableWidth / months.size
                val chartAreaHeight = (HEIGHT - PAD_T - PAD_B).toDouble()

                var globalIdx = 0
                val hits = mutableListOf<BarHit>()
                months.forEachIndexed { gIdx, month ->
                    val rows = byMonth[month].orEmpty()
                    val barAreaWidth = groupWidth - 8
                    val barWidth = (barAreaWidth.toDouble() / rows.size.coerceAtLeast(1)).toInt().coerceIn(8, 48)
                    val groupStartX = PAD_L + gIdx * (groupWidth + groupGap) + 4

                    rows.forEachIndexed { rIdx, u ->
                        val x = groupStartX + rIdx * barWidth
                        val pct = u.participationPct.coerceIn(0, 100)
                        val h = (chartAreaHeight * pct / 100.0).toInt()
                        val yTop = HEIGHT - PAD_B - h
                        val color = if (u.optedOut) Palette.textTertiary.cssRgb() else ChartPalette.at(0)
                        val barIdx = globalIdx

                        SvgRect(attrs = {
                            attr("x", "$x")
                            attr("y", "${HEIGHT - PAD_B - h}")
                            attr("width", "${barWidth - 4}")
                            attr("height", "$h")
                            attr("rx", "3")
                            attr("fill", color)
                            style {
                                property("transform-origin", "${x + (barWidth - 4) / 2}px ${HEIGHT - PAD_B}px")
                                property("transform", if (drawn) "scaleY(1)" else "scaleY(0)")
                                property(
                                    "transition",
                                    "${ChartMotion.intro("transform", barIdx)}, ${ChartMotion.update("y", "height")}",
                                )
                                property("pointer-events", "none")
                            }
                        })
                        hits += BarHit(x = x, width = barWidth - 4, yTop = yTop, color = color, user = u)
                        globalIdx++
                    }

                    SvgText(
                        text = formatMonthLabel(month),
                        attrs = {
                            attr("x", "${groupStartX + groupWidth / 2}")
                            attr("y", "${HEIGHT - PAD_B / 3}")
                            attr("text-anchor", "middle")
                            attr("font-size", "11")
                            attr("font-weight", "500")
                            attr("fill", Palette.textTertiary.cssRgb())
                            attr("font-family", "Inter, sans-serif")
                        },
                    )

                    val avg = if (rows.isNotEmpty()) rows.map { it.participationPct }.average().toInt() else 0
                    SvgText(
                        text = "$avg%",
                        attrs = {
                            attr("x", "${groupStartX + groupWidth / 2}")
                            attr("y", "${PAD_T + 2}")
                            attr("text-anchor", "middle")
                            attr("font-size", "13")
                            attr("font-weight", "700")
                            attr("fill", Palette.textPrimary.cssRgb())
                            attr("font-family", "Inter, sans-serif")
                            attr("style", "font-variant-numeric: tabular-nums; letter-spacing: -0.01em")
                        },
                    )
                }

                hits.forEach { hit ->
                    val u = hit.user
                    SvgRect(attrs = {
                        attr("x", "${hit.x}")
                        attr("y", "$PAD_T")
                        attr("width", "${hit.width}")
                        attr("height", "${HEIGHT - PAD_T - PAD_B}")
                        attr("fill", "transparent")
                        style { property("cursor", "pointer") }
                        onMouseEnter {
                            tooltip = BarHover(
                                x = hit.x + hit.width / 2,
                                y = hit.yTop,
                                content = TooltipContent(
                                    title = u.displayName,
                                    subtitle = formatMonthLabel(u.month),
                                    rows = listOf(
                                        TooltipContent.Row(
                                            label = if (u.optedOut) "Opted out" else "Participation",
                                            value = "${u.participationPct}% (${u.sectionsProvided}/${u.possibleStandups})",
                                            color = hit.color,
                                        ),
                                    ),
                                ),
                            )
                        }
                        onMouseLeave { tooltip = tooltip?.copy(content = null) }
                    })
                }
            }

            val hovered = tooltip
            if (hovered != null) {
                ChartTooltip(
                    content = hovered.content,
                    x = hovered.x,
                    y = hovered.y,
                    visible = hovered.content != null,
                )
            }
        }
    }

    @Suppress("UNUSED_VARIABLE") val unusedTrendSlot = 0
}

private data class BarHover(val x: Int, val y: Int, val content: TooltipContent?)

private data class BarHit(val x: Int, val width: Int, val yTop: Int, val color: String, val user: UserParticipation)

@Composable
private fun renderParticipationGrid() {
    val yBottom = HEIGHT - PAD_B
    val yTop = PAD_T
    val steps = 4
    SvgRect(attrs = {
        attr("x", "$PAD_L")
        attr("y", "$yTop")
        attr("width", "${WIDTH - PAD_L - PAD_R}")
        attr("height", "${yBottom - yTop}")
        attr("fill", Palette.surfaceSubtle.cssRgb())
        attr("opacity", "0.5")
        attr("rx", "6")
    })
    for (i in 0..steps) {
        val y = yBottom - (i * (yBottom - yTop) / steps)
        SvgLine(attrs = {
            attr("x1", "$PAD_L")
            attr("x2", "${WIDTH - PAD_R}")
            attr("y1", "$y")
            attr("y2", "$y")
            attr("stroke", Palette.border.cssRgb())
            attr("stroke-width", "1")
            attr("opacity", if (i == 0) "0.9" else "0.45")
            attr("stroke-dasharray", if (i == 0) "0" else "1 5")
            attr("stroke-linecap", "round")
        })
        val value = i * 25
        SvgText(
            text = "$value%",
            attrs = {
                attr("x", "${PAD_L - 12}")
                attr("y", "${y + 4}")
                attr("text-anchor", "end")
                attr("font-size", "11")
                attr("font-weight", "500")
                attr("fill", Palette.textTertiary.cssRgb())
                attr("font-family", "Inter, sans-serif")
                attr("style", "font-variant-numeric: tabular-nums")
            },
        )
    }
}
